<template>
  <!-- ==============================================================
       GuideTourRecorder — ADMIN authoring tool for the live tour.

       An admin walks the REAL flow; on each step they narrate (mic) what
       they're doing / what's next, optionally MARK the sections the
       narration affects, then hit "Next step". Each step captures:
         • target    — a stable selector for the control they demonstrated
                       (what the customer will click), generalized by id/
                       class so it works on any customer's data
         • highlights — extra selectors the admin marked as "affected"
         • audio      — the per-step voice clip (data URI)
       The finished tour is saved to localStorage (prototype; a backend
       store comes later) and played back by GuideTourWidget.

       SAFETY: while recording, real navigation clicks pass through so the
       page progresses, but cart/order WRITES are intercepted (simulated)
       so the admin never places a real order while recording.

       Launched by the root event 'start-tour-recording' (flow key).
       Mounted once in App.vue. CSS unscoped + namespaced (.gtrec-*).
       ============================================================== -->
  <div class="guide-tour-recorder" v-if="active">
    <!-- brief outline flashes when the admin marks/clicks an element -->
    <div v-for="(f, i) in flashes" :key="i" class="gtrec-flash" :style="f"></div>

    <div class="gtrec-bar" :class="{ 'is-marking': markMode }">
      <div class="gtrec-row">
        <span class="gtrec-dot"></span>
        <span class="gtrec-title">Recording tour — <b>Step {{ steps.length + 1 }}</b></span>
        <span class="gtrec-mic" :class="{ off: !micOn }">{{ micOn ? "🎙 mic on" : "🔇 no mic" }}</span>
      </div>
      <div class="gtrec-hint">
        <template v-if="navNote"><span class="gtrec-note">{{ navNote }}</span></template>
        <template v-else-if="markMode">Marking mode — click the sections this step affects. ({{ step.highlights.length }} marked)</template>
        <template v-else>Click the control you're demonstrating, narrate it, then “Next step”.
          <template v-if="step.target"> · captured: <code>{{ step.target }}</code></template>
        </template>
      </div>
      <div class="gtrec-controls">
        <button type="button" class="gtrec-btn gtrec-btn-ghost" @click="cancel">Cancel</button>
        <span class="gtrec-spacer"></span>
        <button type="button" class="gtrec-btn gtrec-btn-mark" :class="{ on: markMode }" @click="toggleMark">
          {{ markMode ? "Done marking" : "🖍 Mark sections" }}
        </button>
        <button type="button" class="gtrec-btn gtrec-btn-ghost" @click="nextStep" :disabled="!stepHasContent">
          Next step ▸
        </button>
        <button type="button" class="gtrec-btn" @click="finish">Finish &amp; Save</button>
      </div>
    </div>
  </div>
</template>

<script>
function robustSelector(el) {
  if (!el || !el.tagName) return null;
  var esc = function (s) {
    return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  };
  // 1) nearest ancestor (incl self) with an id — best, generalizes by control
  var node = el;
  for (var i = 0; i < 5 && node && node.tagName; i++) {
    if (node.id) return "#" + esc(node.id);
    node = node.parentElement;
  }
  // 2) nearest element with a meaningful (non-state) class
  node = el;
  for (var j = 0; j < 5 && node && node.tagName; j++) {
    if (node.classList && node.classList.length) {
      var cls = Array.prototype.filter.call(node.classList, function (c) {
        return !/(^|-)(active|open|show|shown|selected|hover|focus|disabled|is-|has-|ng-|v-|router-link)/.test(c);
      });
      if (cls.length) return node.tagName.toLowerCase() + "." + esc(cls[0]);
    }
    node = node.parentElement;
  }
  return el.tagName.toLowerCase();
}

var WRITE_HINT = /(add to cart|place order|review and place|save changes)/i;
function isWriteControl(el) {
  var n = el;
  for (var i = 0; i < 5 && n && n.tagName; i++) {
    if (n.classList && (n.classList.contains("cart-icon-svg") || n.classList.contains("add-to-cart-dest-btn"))) return true;
    var t = (n.textContent || "").trim();
    if (t && t.length < 60 && WRITE_HINT.test(t)) return true;
    n = n.parentElement;
  }
  return false;
}

// A click that would NAVIGATE (real <a href>, a nav link, or the cart pill).
// During recording we capture these but DON'T perform them — navigating would
// reload/leave the page and destroy the in-progress recording.
function isNavClick(el) {
  var n = el;
  for (var i = 0; i < 6 && n && n.tagName; i++) {
    if (n.tagName.toLowerCase() === "a") {
      var h = n.getAttribute("href");
      if (h && h !== "#" && h.toLowerCase().indexOf("javascript") !== 0) return true;
    }
    if (n.classList && (n.classList.contains("nav-link") || n.classList.contains("cart-pill"))) return true;
    n = n.parentElement;
  }
  return false;
}

export default {
  name: "GuideTourRecorder",
  data() {
    return {
      active: false,
      flowKey: "",
      steps: [],
      step: { target: null, highlights: [], label: "" },
      markMode: false,
      micOn: false,
      navNote: "",
      flashes: [],
      _stream: null,
      _mr: null,
      _chunks: [],
      _onClick: null,
    };
  },
  computed: {
    stepHasContent() {
      return !!(this.step.target || this.step.highlights.length || this._mr);
    },
  },
  mounted() {
    this._startEvt = this.start.bind(this);
    this.$root.$on("start-tour-recording", this._startEvt);
  },
  beforeDestroy() {
    this.$root.$off("start-tour-recording", this._startEvt);
    this.cleanup();
  },
  methods: {
    async start(flowKey) {
      if (this.active) return;
      this.flowKey = flowKey || "order";
      this.steps = [];
      this.step = { target: null, highlights: [], label: "" };
      this.markMode = false;
      this.active = true;
      // mic (optional — recording still works without it)
      try {
        this._stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.micOn = true;
      } catch (e) {
        this.micOn = false;
      }
      this.startStepRecorder();
      this._onClick = this.onDocClick.bind(this);
      document.addEventListener("click", this._onClick, true);
    },

    onDocClick(e) {
      var t = e.target;
      if (t && t.closest && t.closest(".gtrec-bar")) return; // our own UI
      if (t && t.closest && t.closest(".gtrec-flash")) return;
      var sel = robustSelector(t);
      if (!sel) return;

      if (this.markMode) {
        // Mark this section as affected; don't perform the click.
        e.preventDefault();
        e.stopPropagation();
        if (this.step.highlights.indexOf(sel) === -1) this.step.highlights.push(sel);
        this.flash(t, "#b9770e");
        return;
      }

      // Normal click = the control being demonstrated. Capture it; let it
      // act (page progresses) UNLESS it would navigate (kills the recording)
      // or write (places a real order) — then capture only, don't perform.
      this.step.target = sel;
      this.step.label = (t.textContent || "").trim().slice(0, 40);
      var nav = isNavClick(t);
      if (nav || isWriteControl(t)) {
        e.preventDefault();
        e.stopPropagation();
        this.flash(t, "#c0392b");
        if (nav) {
          this.showNote(
            "Captured — but navigation is paused while recording (you're already on this page). Demonstrate the on-page steps here."
          );
        }
      } else {
        this.flash(t, "#2e7d32");
      }
    },
    showNote(msg) {
      var self = this;
      this.navNote = msg;
      if (this._noteT) clearTimeout(this._noteT);
      this._noteT = setTimeout(function () { self.navNote = ""; }, 4500);
    },

    toggleMark() {
      this.markMode = !this.markMode;
    },

    async nextStep() {
      if (!this.stepHasContent) return;
      var audio = await this.finalizeAudio();
      this.steps.push({
        target: this.step.target,
        highlights: this.step.highlights.slice(),
        label: this.step.label,
        audio: audio,
      });
      this.step = { target: null, highlights: [], label: "" };
      this.markMode = false;
      this.startStepRecorder();
    },

    async finish() {
      // capture the in-progress step if it has anything
      if (this.stepHasContent) {
        var audio = await this.finalizeAudio();
        this.steps.push({
          target: this.step.target,
          highlights: this.step.highlights.slice(),
          label: this.step.label,
          audio: audio,
        });
      }
      var payload = { flow: this.flowKey, steps: this.steps };
      try {
        localStorage.setItem("jcs_tour_rec_" + this.flowKey, JSON.stringify(payload));
      } catch (e) {
        // localStorage may overflow with long audio — warn but keep going.
        alert("Saved the steps, but the audio was too large for local storage. (The backend store will fix this.)");
        payload.steps.forEach(function (s) { s.audio = null; });
        try { localStorage.setItem("jcs_tour_rec_" + this.flowKey, JSON.stringify(payload)); } catch (e2) {}
      }
      this.teardown();
      this.$root.$emit("tour-recording-saved", this.flowKey);
      this.$root.$emit(
        "jcs-toast",
        "Tour saved (" + this.steps.length + " steps). Open the Guide → Live interactive tour to preview it."
      );
    },

    cancel() {
      this.teardown();
    },

    // ---- audio ----
    startStepRecorder() {
      if (!this._stream) return;
      this._chunks = [];
      try {
        this._mr = new MediaRecorder(this._stream);
        var self = this;
        this._mr.ondataavailable = function (e) { if (e.data && e.data.size) self._chunks.push(e.data); };
        this._mr.start();
      } catch (e) {
        this._mr = null;
      }
    },
    finalizeAudio() {
      var self = this;
      return new Promise(function (resolve) {
        var mr = self._mr;
        self._mr = null;
        if (!mr) return resolve(null);
        mr.onstop = function () {
          if (!self._chunks.length) return resolve(null);
          var blob = new Blob(self._chunks, { type: self._chunks[0].type || "audio/webm" });
          var reader = new FileReader();
          reader.onloadend = function () { resolve(reader.result); };
          reader.onerror = function () { resolve(null); };
          reader.readAsDataURL(blob);
        };
        try { mr.stop(); } catch (e) { resolve(null); }
      });
    },

    flash(el, color) {
      try {
        var r = el.getBoundingClientRect();
        var style = {
          left: r.left - 3 + "px",
          top: r.top - 3 + "px",
          width: r.width + 6 + "px",
          height: r.height + 6 + "px",
          borderColor: color || "#c0392b",
        };
        this.flashes.push(style);
        var self = this;
        setTimeout(function () { self.flashes.shift(); }, 900);
      } catch (e) {}
    },

    teardown() {
      this.cleanup();
      this.active = false;
      this.markMode = false;
    },
    cleanup() {
      if (this._onClick) {
        document.removeEventListener("click", this._onClick, true);
        this._onClick = null;
      }
      try { if (this._mr) this._mr.stop(); } catch (e) {}
      this._mr = null;
      if (this._stream) {
        try { this._stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
        this._stream = null;
      }
      if (this._noteT) { clearTimeout(this._noteT); this._noteT = null; }
      this.navNote = "";
      this.flashes = [];
    },
  },
};
</script>

<style>
/* ===== TOUR RECORDER — UNSCOPED, namespaced .gtrec-* ===== */
.gtrec-flash {
  position: fixed;
  z-index: 20050;
  border: 2px solid #c0392b;
  border-radius: 6px;
  pointer-events: none;
  animation: gtrecFlash 0.9s ease-out forwards;
}
@keyframes gtrecFlash {
  0% { opacity: 0.95; }
  100% { opacity: 0; }
}
.gtrec-bar {
  position: fixed;
  z-index: 20060;
  left: 50%;
  bottom: 18px;
  transform: translateX(-50%);
  width: 560px;
  max-width: calc(100vw - 24px);
  background: #1f1f1f;
  color: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.45);
  padding: 12px 16px;
  font-size: 13px;
}
.gtrec-bar.is-marking {
  outline: 2px solid #f0c36d;
}
.gtrec-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.gtrec-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #e53935;
  animation: gtrecPulse 1.1s ease-in-out infinite;
}
@keyframes gtrecPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.gtrec-title { font-weight: 600; }
.gtrec-mic { margin-left: auto; font-size: 11px; color: #8bc34a; }
.gtrec-mic.off { color: #ff8a80; }
.gtrec-hint {
  margin: 7px 0 10px;
  font-size: 12px;
  color: #cfcfcf;
  line-height: 1.4;
}
.gtrec-hint code {
  color: #ffd180;
  font-size: 11px;
}
.gtrec-note { color: #ffd180; font-weight: 600; }
.gtrec-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}
.gtrec-spacer { flex: 1 1 auto; }
.gtrec-btn {
  border: 0;
  border-radius: 6px;
  background: #c0392b;
  color: #fff;
  font-size: 12.5px;
  font-weight: 700;
  padding: 7px 14px;
  cursor: pointer;
}
.gtrec-btn:hover { background: #a02b1f; }
.gtrec-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.gtrec-btn-ghost {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
.gtrec-btn-ghost:hover { background: rgba(255, 255, 255, 0.22); }
.gtrec-btn-mark {
  background: rgba(240, 195, 109, 0.18);
  color: #ffd180;
}
.gtrec-btn-mark.on {
  background: #f0c36d;
  color: #1f1f1f;
}
</style>
